import * as React from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { MatterStatus } from '@/services/types/client-intake-types';

interface MatterStatusNoteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (note: string) => Promise<void>;
  targetStatus: MatterStatus;
  matterName: string;
}

/**
 * Modal that requires a note when changing a matter to a terminal status
 * (Complete, Client Dropped, Hidden).
 */
export const MatterStatusNoteModal: React.FC<MatterStatusNoteModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  targetStatus,
  matterName,
}) => {
  const [note, setNote] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const canSubmit = note.trim() !== '';

  // Reset form when modal closes
  React.useEffect(() => {
    if (!isOpen) {
      setNote('');
      setIsSubmitting(false);
    }
  }, [isOpen]);

  const handleConfirm = async () => {
    if (!canSubmit) return;

    setIsSubmitting(true);
    try {
      await onConfirm(note.trim());
    } catch (error) {
      // Error handling is done in parent component
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  // Handle Cmd/Ctrl+Enter to submit
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (
      e.key === 'Enter' &&
      (e.metaKey || e.ctrlKey) &&
      canSubmit &&
      !isSubmitting
    ) {
      e.preventDefault();
      handleConfirm();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Add Status Change Note</DialogTitle>
          <DialogDescription>
            Please explain why you are changing &ldquo;{matterName}&rdquo; to{' '}
            <strong>{targetStatus}</strong>. This note will appear in the
            activity feed.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="status-change-note">
              Note <span className="text-red-500 dark:text-red-400">*</span>
            </Label>
            <Textarea
              id="status-change-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Enter the reason for this status change..."
              rows={4}
              className="resize-none"
              aria-required
              aria-invalid={!note.trim()}
              autoFocus
            />
            {!note.trim() && (
              <p className="text-xs text-muted-foreground">
                A note is required to change the status to {targetStatus}
              </p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={!canSubmit || isSubmitting}>
            {isSubmitting ? 'Saving...' : 'Save & Update Status'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
